<template>
	<div>
		<ClientOnly>
			<div v-if="isBlocking" class="horizontal-title-list-blocking">
				<div class="blocking-element-container" v-for="tmp in [1, 2, 3]" :key="tmp">
					<div class="blocking-element blocking-title-poster"></div>
					<div v-if="showPriceDrop" class="blocking-element blocking-discount-label"></div>
					<div v-if="showPriceDrop" class="blocking-element blocking-offer-button"></div>
				</div>
			</div>
		</ClientOnly>

		<div v-if="!isBlocking" class="horizontal-title-list">
			<HiddenHorizontalScrollbar
				ref="horizontal-scrollbar"
				:allItemsLoaded="maxTitles === 0 || maxTitles === titles.length"
				:additionalContexts="additionalContexts"
				:resetScrollOnChange="resetScrollOnChange"
				:emitOnScroll="emitOnScroll"
				:fadedEdge="fadedEdge"
				@onScrollRight="scrolledRight"
				@scrollbarClicked="$emit('scrollbarClicked', $event)"
			>
				<slot name="sponsoredRecommendation" />

				<!-- FIXME DRY -->
				<div
					class="horizontal-title-list__item"
					:class="{ 'horizontal-title-list__item--with-position': showPosition }"
					v-for="(title, index) in titles"
					:key="`${title.id}-${title.objectType}`"
				>
					<div
						v-if="showPosition"
						class="horizontal-title-list__item-position"
						:style="{ color: getPositionColor(index + 1) }"
					>
						{{ index + 1 }}
					</div>

					<TitleDetailLink
						:fullPath="title.content.fullPath"
						:title="title"
						@clickOut="handleClickOut({ title, position: index })"
					>
						<div @click="clicked($event, index, title)">
							<slot
								name="poster"
								:title="title"
								:index="index"
								:props="getPosterGraphqlProps(title, index)"
								:handlers="getPosterGraphqlEvents(title, index)"
							>
								<PosterGraphql
									v-bind="getPosterGraphqlProps(title, index)"
									v-on="getPosterGraphqlEvents(title, index)"
								/>
							</slot>
							<!-- used for season listing -->
							<div v-if="showTitle" class="horizontal-title-list__item__caption">
								<slot name="listing" :title="title">
									{{ $t('WEBAPP_SEASON') }} {{ getSeasonNumber(title) }}
								</slot>
							</div>
						</div>
					</TitleDetailLink>

					<!-- used for price drops -->
					<TitleCardPricechange
						v-if="showPriceDrop"
						:priceChangeOffer="title.watchNowOffer"
						:titleObjectId="getTitleObjectId(title)"
						:titleObjectType="getTitleObjectType(title)"
						:titleSeasonNumber="getSeasonNumber(title)"
						:jwRawEntityId="title.id"
					/>
				</div>
				<!-- possible to add content after -->
				<slot name="after" />
			</HiddenHorizontalScrollbar>
		</div>
	</div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';

import PosterGraphql from '@/components/poster/PosterGraphql.vue';
import TitleDetailLink from '@/components/nav/links/TitleDetailLink.vue';
import HiddenHorizontalScrollbar from '@/components/HiddenHorizontalScrollbar.vue';

import { ImpressionTrackingEvents } from '@/enums/events';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';

import { useBackendTracking } from '@/helpers/composables/useBackendTracking';

import { SraEventAction } from '@/@types/graphql-types';
import type { SnowplowContext } from '@/helpers/tracking/providers/snowplow-contexts';
import type { PosterGraphqlProps } from '@/interfaces/components/poster/PosterGraphql';
import type { SponsoredAdFragment } from '@/features/sponsored-recommendations/graphql/fragments/SponsoredAd.fragment';
import type {
	HorizontalTitleListItem,
	PosterClickEventPayload,
	PosterClickOutEventPayload,
	HorizontalTitleListItemPayload,
} from './types';
import { useUserStore } from '@/stores/pinia/useUserStore';
import { storeToRefs } from 'pinia';

const TitleCardPricechange = defineAsyncComponent(
	() => import(/* webpackChunkName: "title-card-pricechange" */ '@/components/list/TitleCardPricechange.vue')
);

const props = withDefaults(
	defineProps<{
		clickSource?: string;
		titles?: HorizontalTitleListItem[];
		sponsoredAd?: SponsoredAdFragment;
		maxTitles?: number;
		showRibbon?: boolean;
		showCheck?: boolean;
		showQuickActions?: boolean;
		forceQuickActions?: boolean;
		hasWatchNow?: boolean;
		showPriceDrop?: boolean;
		showTitle?: boolean;
		showTvBadge?: boolean;
		isBlocking?: boolean;
		smallPosters?: boolean;
		preventDefault?: boolean;
		resetScrollOnChange?: boolean;
		hasMoreLikeThis?: boolean;
		additionalContexts?: SnowplowContext[];
		imgsLoadCount?: number;
		emitOnScroll?: boolean;
		showPosition?: boolean;
		showPositionColor?: 'primary' | 'brand';
		fadedEdge?: boolean;
		isPressPage?: boolean;
		showFreeTag?: boolean;
		/** Optional string to group poster impressions by. */
		moduleGroup?: string;
		loading?: 'lazy' | 'eager';
	}>(),
	{
		clickSource: '',
		titles: () => [],
		maxTitles: 0,
		hasWatchNow: true,
		showTvBadge: true,
		additionalContexts: () => [],
		imgsLoadCount: 0,
		showPositionColor: 'primary',
		showFreeTag: false,
		moduleGroup: '',
		loading: 'eager',
	}
);

const emit = defineEmits<{
	(e: 'mounted'): void;
	(e: 'onScrollRight', complete: () => void): void;
	(e: 'scrollbarClicked', label: 'left' | 'right'): void;
	(e: 'clicked', data: HorizontalTitleListItemPayload): void;
	(e: 'hovered', data: HorizontalTitleListItemPayload): void;
	(e: 'openModal', data: HorizontalTitleListItemPayload): void;
	(e: 'onPosterClickOut', data: PosterClickOutEventPayload): void;
	(e: 'onPosterClick', data: PosterClickEventPayload): void;
}>();

const userStore = storeToRefs(useUserStore());

const isRibbonVisible = computed(() => {
	if (props.showRibbon) {
		return props.showRibbon;
	}

	// make sure we can disable the ribbon specifically
	if (!props.showRibbon && !props.showQuickActions) {
		return false;
	}

	return !userStore.isLoggedIn.value;
});

const sponsoredRecommendationPackageId = computed(() => props.sponsoredAd?.campaign?.watchNowOffer.package.packageId);

const sponsoredRecommendationTitleList = computed(() => props.sponsoredAd?.campaign?.node.nodeId);

function getPositionColor(position: number) {
	if (props.isPressPage) {
		return '#060d18';
	}

	if (props.showPositionColor === 'primary') {
		return '#222c38';
	}

	let numberColor = '#222c38';

	switch (position) {
		case 1:
			numberColor = '#ffd700';
			break;
		case 2:
			numberColor = '#c0c0c0';
			break;
		case 3:
			numberColor = '#cd7f32';
			break;
	}

	return numberColor;
}

function handleClickOut({ title, position }: { title: any; position: number }) {
	emit('onPosterClickOut', {
		index: position,
		title,
		titleList: sponsoredRecommendationTitleList.value,
		event: SraEventAction.Clickout,
	});
}

function handlePosterClick(index: number, title: HorizontalTitleListItem, titleList?: string) {
	emit('onPosterClick', { index, title, titleList });

	if (!!sponsoredRecommendationPackageId.value && props.sponsoredAd?.campaign?.node.nodeId === title.id) {
		const label =
			(props.sponsoredAd?.campaign?.node as any)?.type === 'TECHNICAL_USER_LIST' && titleList
				? `${titleList}_${sponsoredRecommendationPackageId.value}`
				: `${title.id}_${sponsoredRecommendationPackageId.value}`;

		TrackingHelper.trackEvent(ImpressionTrackingEvents.SPONSORED_RECOMMENDATIONS, {
			action: 'click',
			label,
			property: 'poster',
		});
	}
}

const { trackSraEvent } = useBackendTracking();
function impressionCallback(title: HorizontalTitleListItem) {
	const titleId = title.__typename === 'Season' ? title.show.id : title.id;

	if (props.sponsoredAd?.campaign?.node.nodeId === titleId) {
		return trackSraEvent({ action: SraEventAction.Impression, sponsoredAd: () => props.sponsoredAd });
	}
}

function getPosterGraphqlEvents(title: HorizontalTitleListItem, index: number) {
	return {
		impressionCallback: () => impressionCallback(title),
		moreInfosClicked: () => navigateTitleGraphql(title),
		openModal: () => emit('openModal', { index, title }),
		hovered: () => emit('hovered', { index, title }),
		click: () => handlePosterClick(index, title, sponsoredRecommendationTitleList.value),
	};
}

const isMounted = ref(false);
onMounted(() => {
	isMounted.value = true;
	emit('mounted');
});

function getPosterGraphqlProps(title: HorizontalTitleListItem, index: number): PosterGraphqlProps {
	return {
		title: title,
		watchNowOffer: title.watchNowOffer,
		showRibbon: isRibbonVisible.value,
		showQuickActions: props.showQuickActions && userStore.isLoggedIn.value && isMounted.value,
		forceQuickActions: props.forceQuickActions,
		hasMoreLikeThis: props.hasMoreLikeThis,
		showWatchNowButton: props.hasWatchNow && !props.showPriceDrop,
		showTvBadge: props.showTvBadge,
		showNewEpisodesCount: title.watchNowOffer != null && props.showTvBadge,
		clickSource: props.clickSource,
		lazy: index >= props.imgsLoadCount,
		showAvailabilityBadge: true,
		availabilityBadgeSize: 'short',
		noRadius: props.showPriceDrop ? 'bottom' : undefined,
		additionalContexts: props.additionalContexts,
		showFreeTag: props.showFreeTag,
		index,
		moduleGroup: props.moduleGroup,
		loading: props.loading,
	};
}

const isReady = ref(true);
function complete() {
	isReady.value = true;
}

function scrolledRight() {
	if (isReady.value && props.titles.length > 0 && props.titles.length < props.maxTitles) {
		isReady.value = false;
		emit('onScrollRight', complete);
	}
}

const router = useRouter();
function navigateTitleGraphql(title: HorizontalTitleListItem) {
	router.push({ path: title.content.fullPath });
}

/** handle click event coming from Poster */
function clicked(evt: Event, index: number, title: HorizontalTitleListItem) {
	const { className = '', nodeName = '' } = evt.target as HTMLElement;
	const ignoreClassName: string[] = [];
	const ignoreNode = ['SVG', 'PATH'];

	// ignore click from font icons
	if (ignoreNode.indexOf(nodeName.toUpperCase()) !== -1) {
		evt.preventDefault();
		evt.stopPropagation();
		return;
	}

	// ignore click when from ignoreClassName elements
	if (ignoreClassName.indexOf(className) !== -1) {
		evt.preventDefault();
		evt.stopPropagation();
		return;
	}

	// emit click from img
	if (nodeName.toUpperCase() === 'IMG') {
		if (props.preventDefault) {
			// prevent img default behavior
			evt.preventDefault();
			evt.stopPropagation();
		}
		emit('clicked', { index, title });
	}
}

function getTitleObjectId(title: HorizontalTitleListItem) {
	return title.__typename === 'Season' ? title.show?.objectId : title.objectId;
}

function getTitleObjectType(title: HorizontalTitleListItem) {
	return title.__typename === 'Season' ? title?.show?.objectType : title.objectType;
}

function getSeasonNumber(title: HorizontalTitleListItem) {
	return title.__typename === 'Season' ? title.content.seasonNumber : undefined;
}
</script>

<style lang="scss">
@import '~@/styles/variables.scss';
@import '~@/styles/mixins.scss';
@import '@/styles/sports.scss';

.horizontal-title-list-blocking {
	display: flex;
	margin-top: 10px;

	.blocking-element-container {
		display: inline-block;
		margin-right: 10px;
		border-radius: 5px;
		@include title-poster-width();

		.blocking-title-poster {
			@include title-poster-height();
		}

		.blocking-discount-label {
			height: 26px;
		}

		.blocking-offer-button {
			height: 40px;
		}
	}
}

.horizontal-title-list {
	$parent: &;

	--margin-top: 10px;
	margin-top: var(--margin-top);

	&__item--with-position {
		padding-bottom: 32px;
		margin-bottom: -32px;
		display: flex;
		align-items: flex-end;

		#{$parent}__item-position {
			height: 100%;
			line-height: 100%;
			font-size: 180px;
			letter-spacing: -20px;
			margin-inline-end: -2px;
			font-weight: 900;
			margin-inline-start: -4px;
		}
	}

	&__item {
		margin: 0 5px 0;
		position: relative;

		z-index: 10;

		&__caption {
			margin: 5px;
			overflow: hidden;
			max-width: 155px;
			white-space: nowrap;
			text-overflow: ellipsis;
		}

		&:first-child {
			margin-inline-start: 0;
		}
	}

	.title-poster {
		@include title-poster-width();
	}

	// On mobile, make posters smaller for SR to fit and avoid cropping
	@media (max-width: $screen-xs-max) {
		&:has(.sponsored-recommendation--IMAGE.sponsored-recommendation__list-row) .title-poster {
			width: 168px;
		}
	}

	.timeline & {
		margin-inline-start: -35px;

		.hidden-horizontal-scrollbar__items {
			padding-left: 35px;
			padding-right: 35px;

			/* full width style for mobile viewports */
			@media (max-width: $screen-xs-max) {
				padding-inline-start: 50px;
			}
		}
	}
}

.pointer-none {
	pointer-events: none;
}
</style>
